/* GENERATED from microlab (mobile) src/modules/anatomyRecall — keep in sync; edit the mobile .ts source and regenerate. */
/*
  AnswerValidator — typed anatomical names.

  Trace  : strict, character-level guidance against the canonical name.
  Recall / Master : whole-answer validation against canonical name +
                    synonyms, with small typo tolerance that NEVER lets a
                    *different* structure slip through (e.g. "teres minor"
                    for "teres major", "C4" for "C3", "ileum" for "ilium").
*/
import { RECALL_CARDS } from './cards';
/* ───────────── normalisation ───────────── */

export function normalizeAnswer(value) {
  return String(value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[’'`]/g, '').replace(/\((left|right|midline|both)\)\s*$/g, '').replace(/[^a-z0-9]+/g, ' ').replace(/^the\s+/, '').replace(/\s+/g, ' ').trim();
}
const GENERIC_SUFFIX = /\s+(bone|muscle|muscles)$/;

/** Accept "scaphoid" for "scaphoid bone" and vice versa. */
function variants(normalized) {
  const out = new Set([normalized]);
  const stripped = normalized.replace(GENERIC_SUFFIX, '');
  if (stripped.length >= 4) out.add(stripped);
  return [...out];
}

/* Words that distinguish neighbouring structures. If the target has one,
   the answer must contain exactly the same word — no typo tolerance. */
const DISCRIMINATORS = new Set(['major', 'minor', 'superior', 'inferior', 'anterior', 'posterior', 'medial', 'lateral', 'intermediate', 'middle', 'internal', 'external', 'innermost', 'left', 'right', 'longus', 'brevis', 'magnus', 'maximus', 'medius', 'minimus', 'superficial', 'deep', 'ascending', 'descending', 'transverse', 'proximal', 'distal', 'common', 'thoracic', 'abdominal', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth', 'eleventh', 'twelfth', 'great', 'thumb', 'index', 'ring', 'little', 'toe', 'finger']);
const ROMAN = /^(i|ii|iii|iv|v|vi|vii|viii|ix|x|xi|xii)$/;
function strictTokens(normalized) {
  return normalized.split(' ').filter(token => DISCRIMINATORS.has(token) || /\d/.test(token) || ROMAN.test(token));
}
function sameStrictTokens(target, input) {
  const want = strictTokens(target);
  if (!want.length) return true;
  const have = new Set(input.split(' '));
  if (!want.every(token => have.has(token))) return false;
  // The answer must not add conflicting discriminators either.
  const extra = strictTokens(input).filter(token => !want.includes(token));
  return extra.length === 0;
}

/* Optimal-string-alignment distance (Damerau–Levenshtein, adjacent swaps). */
export function editDistance(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const rows = a.length + 1;
  const cols = b.length + 1;
  const d = Array.from({
    length: rows
  }, (_, i) => {
    const row = new Array(cols).fill(0);
    row[0] = i;
    return row;
  });
  for (let j = 0; j < cols; j += 1) d[0][j] = j;
  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[rows - 1][cols - 1];
}
function tolerance(length) {
  if (length <= 4) return 0;
  if (length <= 8) return 1;
  if (length <= 14) return 2;
  return 3;
}

/* Every name/synonym known to Anatomy Recall, mapped to the structure
   key(s) it belongs to. Used to reject answers that are really another
   structure. */
let NAME_INDEX = null;
function nameIndex() {
  if (NAME_INDEX) return NAME_INDEX;
  const index = new Map();
  RECALL_CARDS.forEach(card => card.items.forEach(entry => {
    acceptedForms(entry).forEach(form => {
      const keys = index.get(form) || new Set();
      keys.add(entry.key);
      index.set(form, keys);
    });
  }));
  NAME_INDEX = index;
  return index;
}
export function acceptedForms(entry) {
  const forms = new Set();
  [entry.name, ...(entry.synonyms || [])].forEach(raw => {
    variants(normalizeAnswer(raw)).forEach(form => form && forms.add(form));
  });
  return [...forms];
}
export function validateAnswer(input, entry) {
  const canonical = entry.name;
  const typed = normalizeAnswer(input);
  if (!typed) return {
    outcome: 'wrong',
    typo: false,
    canonical
  };
  const typedForms = variants(typed);
  const targets = acceptedForms(entry);

  // 1. Exact (after normalisation).
  if (typedForms.some(form => targets.includes(form))) {
    return {
      outcome: 'correct',
      typo: false,
      canonical
    };
  }

  // 2. Exactly another known structure → wrong, never "close".
  const index = nameIndex();
  const isOther = typedForms.some(form => {
    const keys = index.get(form);
    return keys && !keys.has(entry.key);
  });
  if (isOther) return {
    outcome: 'wrong',
    typo: false,
    canonical
  };

  // 3. Typo tolerance, guarded by discriminating tokens and by "is some
  //    other structure at least as close?".
  let best = Infinity;
  let bestTarget = '';
  typedForms.forEach(form => targets.forEach(target => {
    if (!sameStrictTokens(target, form)) return;
    const dist = editDistance(form, target);
    if (dist < best) {
      best = dist;
      bestTarget = target;
    }
  }));
  if (!bestTarget) return {
    outcome: 'wrong',
    typo: false,
    canonical
  };
  const allowed = tolerance(bestTarget.length);
  if (best <= allowed) {
    let rivalCloser = false;
    index.forEach((keys, form) => {
      if (rivalCloser || keys.has(entry.key)) return;
      if (Math.abs(form.length - typed.length) > best) return;
      if (typedForms.some(t => editDistance(t, form) <= best)) rivalCloser = true;
    });
    if (!rivalCloser) return {
      outcome: 'correct',
      typo: best > 0,
      canonical
    };
    return {
      outcome: 'close',
      typo: false,
      canonical
    };
  }
  if (best <= allowed + 2) return {
    outcome: 'close',
    typo: false,
    canonical
  };
  return {
    outcome: 'wrong',
    typo: false,
    canonical
  };
}

/* ───────────── Trace helpers ───────────── */

function foldChar(ch) {
  return ch.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Trace: returns how many leading characters of `typed` match `canonical`
 * (case/diacritic-insensitive). Anything past that is a mistake.
 */
export function tracePrefixLength(typed, canonical) {
  let i = 0;
  while (i < typed.length && i < canonical.length) {
    if (foldChar(typed[i]) !== foldChar(canonical[i])) break;
    i += 1;
  }
  return i;
}

/** Hint text: level 1 = first letter + mask, level 2 = first half. */
export function hintFor(canonical, level) {
  if (level <= 0) return '';
  const reveal = level === 1 ? 1 : Math.max(2, Math.ceil(canonical.length / 2));
  return canonical.split('').map((ch, i) => i < reveal || ch === ' ' ? ch : '•').join('');
}
